import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { DrawingThumbnail, drawingThumbnailSvg } from '../DrawingThumbnail';
import { StructureZoomDialog, ZoomablePicture, type ZoomPicture } from '../StructureZoomDialog';
import { useDragSource } from '../../dnd/DragDrop';
import type { PaperPlacement, StructureDragPayload } from '../../dnd/payloads';
import { usePaperAdditions } from './paperAdditions';
import { AddToPaperButton, useAddToPaper } from './AddToPaperButton';
import type { DrawingCard } from './searchResultsModel';

/**
 * #794 -- a saved drawing that is more than its molecule (text, shapes, arrows...) as a result of
 * its own, under Show -> Drawings: the drawing as saved, its molecule's match, and the same
 * actions as a My drawings card (add to paper, drag onto it, open).
 */
export function DrawingResultCard({
  card,
  scoreLabel,
  onOpen,
  openDisabled
}: {
  readonly card: DrawingCard;
  /** The overlay's score line (its molecule's match). */
  readonly scoreLabel: ReactNode;
  readonly onOpen?: ((id: string) => void) | undefined;
  readonly openDisabled?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const name = card.label.trim() || t('myDrawings.untitled');
  const additions = usePaperAdditions();
  const addDrawing = useMemo(
    () =>
      additions
        ? async (_isomerId?: string, placement?: PaperPlacement) => {
            await additions.addDrawing(card.drawingId, placement);
            return null;
          }
        : undefined,
    [additions, card.drawingId]
  );
  const adding = useAddToPaper(addDrawing);
  const { add } = adding;
  const payload = useMemo<StructureDragPayload | null>(
    () => (additions ? { kind: 'structure', label: name, place: (placement) => void add(undefined, placement) } : null),
    [add, additions, name]
  );
  const drag = useDragSource(payload, <span className="dnd-ghost-chip">{name}</span>);
  // #984 -- click or tap the drawing to see it larger.
  const [enlarged, setEnlarged] = useState(false);
  const picture = useMemo<ZoomPicture>(
    () => ({ kind: 'svg', load: () => drawingThumbnailSvg(card.drawingId, card.updatedAt ?? undefined, 'paper') }),
    [card.drawingId, card.updatedAt]
  );
  return (
    <div className="search-result-card drawing-result-card" data-testid="drawing-result-card" {...drag}>
      <div className="search-result-thumb">
        <ZoomablePicture name={name} onOpen={() => setEnlarged(true)}>
          <DrawingThumbnail drawingId={card.drawingId} version={card.updatedAt ?? undefined} size={72} ink="paper" />
        </ZoomablePicture>
      </div>
      {enlarged ? (
        <StructureZoomDialog
          picture={picture}
          name={name}
          matchLabel={scoreLabel}
          actions={
            additions ? (
              <AddToPaperButton moleculeId="" adding={adding} boxes={null} onAdded={() => setEnlarged(false)} />
            ) : null
          }
          onClose={() => setEnlarged(false)}
        />
      ) : null}
      <div className="search-result-card-overlay-top">{scoreLabel}</div>
      <div className="search-result-card-footer">
        <Icon name="draw" size="1.1rem" />
        <span className="search-results-id" data-testid="result-name">
          {name}
        </span>
        {additions ? <AddToPaperButton moleculeId="" adding={adding} boxes={null} /> : null}
        {onOpen ? (
          <div className="result-container-button">
            <button
              type="button"
              className="result-container-trigger"
              data-testid="drawing-result-open"
              aria-label={t('drawingResults.open', { title: name })}
              title={t('drawingResults.open', { title: name })}
              disabled={openDisabled}
              onClick={() => onOpen(card.drawingId)}
            >
              <Icon name="openInNew" size="1.1rem" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
